import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { voiceActorService } from '../../../services/voiceActorService';
import { RealtimeSubscriptionService } from '../../../services/realtimeSubscriptionService';
import UserAvatar from '../../../components/UserAvatar';

const PerformersSection = () => {
  const navigate = useNavigate();
  const [performers, setPerformers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [realtimeService] = useState(() => new RealtimeSubscriptionService());

  useEffect(() => {
    loadPerformers();

    // Subscribe to real-time updates for voice actors
    const unsubscribe = realtimeService?.subscribeToVoiceActors((payload) => {
      console.log('Real-time voice actor update:', payload);
      // Reload performers when any change occurs
      loadPerformers();
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const loadPerformers = async () => {
    try {
      const allVoiceActors = await voiceActorService?.getAllVoiceActors();
      
      // Exclude specific performers from homepage listing
      const excludedNames = ['Bradley Bundlie', 'Bowie Bundlie', 'Samantha Leeds'];
      const filteredPerformers = allVoiceActors?.filter(
        performer => !excludedNames?.includes(performer?.stageName)
      ) || [];
      
      setPerformers(filteredPerformers);
    } catch (error) {
      console.error('Error loading performers:', error);
      setError('Failed to load performers');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePerformerClick = (performerId) => {
    navigate(`/performer-profile/${performerId}`);
  };

  if (isLoading) {
    return (
      <section className="py-24 lg:py-32 relative bg-black/20">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent mb-4"></div>
            <p className="text-white/70">Loading performers...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-24 lg:py-32 relative bg-black/20">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-red-400 mx-auto mb-4" />
            <p className="text-white/70">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 lg:py-32 pt-12 lg:pt-16 relative bg-black/20">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            Meet Our <span className="gradient-text">Talented Performers</span>
          </h2>
          <p className="text-base sm:text-lg text-white/70 max-w-3xl mx-auto">
            Behind every line of Shakespeare lies the magic of our incredible voice actors! These talented performers breathe life into the Bard's words, delivering exciting and proper performance readings that transform each play into an immersive theatrical experience. From dramatic soliloquies to witty banter, our performers ensure every character leaps off the page and into your imagination.
          </p>
        </div>

        {performers?.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {performers?.map((performer) => (
              <div
                key={performer?.id}
                onClick={() => handlePerformerClick(performer?.id)}
                className="modern-card group hover:scale-105 transition-all duration-300 text-center cursor-pointer"
              >
                <div className="flex flex-col items-center">
                  {performer?.headshotUrl ? (
                    <img
                      src={performer?.headshotUrl}
                      alt={performer?.fullName || performer?.stageName}
                      className="w-24 h-24 rounded-full object-cover mb-4 border-4 border-purple-500/30 group-hover:border-purple-500 transition-colors"
                    />
                  ) : (
                    <UserAvatar
                      userId={performer?.id}
                      name={performer?.fullName || performer?.stageName}
                      size="w-24 h-24"
                      textSize="text-3xl"
                      className="mb-4 border-4 border-purple-500/30 group-hover:border-purple-500 transition-colors"
                    />
                  )}
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">
                    {performer?.fullName || performer?.stageName}
                  </h3>
                  <div className="flex items-center gap-2 text-white/60 text-sm">
                    <Icon name="Mic" size={16} />
                    <span>Voice Actor</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="Users" size={64} className="text-white/30 mx-auto mb-4" />
            <p className="text-white/70">No performers available at the moment</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default PerformersSection;